import { useEffect, useState } from "react";
import { createPortal } from "react-dom";

/**
 * SproutBurst
 * -----------
 * A brief, celebratory botanical flourish shown the moment a user finishes
 * planting a seed. Line-art style to match the Willoah logo/banner.
 *
 * Motion arc (~1.5s):
 *   0.00s  – Soil line draws in (short horizontal stroke)
 *   0.20s  – Stem draws upward from soil
 *   0.55s  – Left leaf unfurls (rotate + scale + fade)
 *   0.75s  – Right leaf unfurls
 *   1.05s  – Optional tiny dew dot on the tip
 *   1.20s  – Everything fades out; portal unmounts at 1.5s
 *
 * Rules honoured:
 *   • Non-blocking — this renders in a portal above everything but is
 *     `pointer-events: none`. The user can already be navigating; the
 *     burst just plays over the top and clears itself.
 *   • Auto-dismisses — no dismiss button; caller doesn't need to babysit
 *     unmount timing, but we still fire `onDone` when the arc finishes.
 *   • `prefers-reduced-motion` — skip the burst entirely and call `onDone`
 *     on next tick so the plant flow still completes cleanly.
 */

export default function SproutBurst({ open, onDone }) {
  const [alive, setAlive] = useState(false);

  useEffect(() => {
    if (!open) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      onDone?.();
      return;
    }
    setAlive(true);
    const t1 = setTimeout(() => onDone?.(), 1200);
    const t2 = setTimeout(() => setAlive(false), 1500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [open, onDone]);

  if (!open || !alive) return null;

  return createPortal(
    <div
      data-testid="sprout-burst"
      className="pointer-events-none fixed inset-0 z-[80] flex items-center justify-center"
      aria-hidden="true"
    >
      <div className="relative">
        <svg
          viewBox="0 0 200 200"
          width="200"
          height="200"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Soil line */}
          <path
            d="M60 145 Q100 138 140 145"
            stroke="#B79256"
            strokeWidth="2.2"
            strokeLinecap="round"
            className="sb-soil"
          />
          {/* Stem — curves up from the middle of the soil */}
          <path
            d="M100 145 C 100 120, 98 100, 100 78"
            stroke="#4E5A3D"
            strokeWidth="2.6"
            strokeLinecap="round"
            className="sb-stem"
          />
          {/* Left leaf */}
          <path
            d="M100 108 C 82 100, 70 108, 68 118 C 82 122, 96 118, 100 112 Z"
            fill="#4E5A3D"
            fillOpacity="0.85"
            className="sb-leaf sb-leaf-left"
          />
          {/* Right leaf */}
          <path
            d="M100 96 C 118 88, 130 96, 132 106 C 118 110, 104 106, 100 100 Z"
            fill="#4E5A3D"
            fillOpacity="0.85"
            className="sb-leaf sb-leaf-right"
          />
          {/* Tip flourish — small circle like a dew drop at the top */}
          <circle
            cx="100"
            cy="76"
            r="3"
            fill="#B79256"
            className="sb-tip"
          />
        </svg>
      </div>
      <style>{`
        .sb-soil {
          stroke-dasharray: 90;
          stroke-dashoffset: 90;
          animation: sb-draw 260ms ease-out forwards,
                     sb-fade 260ms ease 1200ms forwards;
        }
        .sb-stem {
          stroke-dasharray: 100;
          stroke-dashoffset: 100;
          animation: sb-draw 420ms cubic-bezier(0.5, 0, 0.2, 1) 200ms forwards,
                     sb-fade 260ms ease 1200ms forwards;
        }
        .sb-leaf {
          transform-box: fill-box;
          opacity: 0;
        }
        .sb-leaf-left {
          transform-origin: 100% 50%;
          transform: rotate(28deg) scale(0.4);
          animation: sb-leaf-unfurl 380ms cubic-bezier(0.34, 1.56, 0.64, 1) 550ms forwards,
                     sb-fade 260ms ease 1200ms forwards;
        }
        .sb-leaf-right {
          transform-origin: 0% 50%;
          transform: rotate(-28deg) scale(0.4);
          animation: sb-leaf-unfurl 380ms cubic-bezier(0.34, 1.56, 0.64, 1) 750ms forwards,
                     sb-fade 260ms ease 1200ms forwards;
        }
        .sb-tip {
          opacity: 0;
          transform-box: fill-box;
          transform-origin: 50% 100%;
          transform: scale(0.2);
          animation: sb-tip-pop 320ms cubic-bezier(0.34, 1.56, 0.64, 1) 1050ms forwards,
                     sb-fade 260ms ease 1200ms forwards;
        }
        @keyframes sb-draw { to { stroke-dashoffset: 0; } }
        @keyframes sb-leaf-unfurl {
          to { opacity: 1; transform: rotate(0deg) scale(1); }
        }
        @keyframes sb-tip-pop {
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes sb-fade { to { opacity: 0; } }
      `}</style>
    </div>,
    document.body,
  );
}
